import { paths, definitions } from '@luxuryescapes/contract-cart'
import { nonNullable } from 'lib/array/arrayUtils'
import { reportClientError } from 'services/errorReportingService'
import { SUPPORTED_BENEFIT_TYPES } from 'luxPlus/constants/benefits'
import { mapToOfferType } from 'api/cartOfferComparison'

// API response types
type MultiCartOfferComparisonResponse = paths['/api/carts/cart-offer-comparison/multi-carts']['get']['responses']['200']['content']['application/json']
type CartOfferComparisonItem = NonNullable<NonNullable<MultiCartOfferComparisonResponse['result']>[number]['items']>[number]
type MultiCartGetResponse = paths['/api/carts/multi-carts']['get']['responses']['200']['content']['application/json']
type MultiCartCreateResponse = paths['/api/carts/multi-carts']['post']['responses']['201']['content']['application/json']
type MultiCartItemGetResponse = paths['/api/carts/multi-carts/{multiCartId}/items']['get']['responses']['200']['content']['application/json']
type MultiCartGetMemberResponse = paths['/api/carts/multi-carts/{multiCartId}/users']['get']['responses']['200']['content']['application/json']
type MultiCartRevokeUserAccessResponse = paths['/api/carts/multi-carts/{multiCartId}/users/revoke']['patch']['responses']['200']['content']['application/json']
type MultiCartAddItemResponse = paths['/api/carts/multi-carts/{multiCartId}/items']['post']['responses']['201']['content']['application/json']
type MultiCartUpdateItemResponse = paths['/api/carts/multi-carts/{multiCartId}/items']['put']['responses']['200']['content']['application/json']
// Base types
type MultiCartItem = paths['/api/carts/multi-carts/{multiCartId}/items']['get']['responses']['200']['content']['application/json']['result']['items']['0']
type MultiCartAuthUser = NonNullable<MultiCartItem['createdBy']>
// Item types
type MultiCartLuxuryPlusSubscriptionItem = definitions['userCartLuxuryPlusSubscriptionItem']
type MultiCartExperienceItem = definitions['userCartExperienceItem']
type MultiCartTransferItem = definitions['userCartAirportTransferItem']
type MultiCartBedbankHotelItem = definitions['multiCartBedbankItem']
type MultiCartHotelItem = definitions['multiCartHotelItem']
type MultiCartMerchandiseItem = definitions['userCartMerchandiseItem']
// Comment types
type MultiCartCommentResponse = paths['/api/carts/multi-carts/{multiCartId}/comments']['get']['responses']['200']['content']['application/json']
type MultiCartCreateCommentResponse = paths['/api/carts/multi-carts/{multiCartId}/items/{itemId}/comments']['post']['responses']['201']['content']['application/json']
type MultiCartUpdateCommentResponse = paths['/api/carts/multi-carts/{multiCartId}/comments/{commentId}']['put']['responses']['200']['content']['application/json']
type MultiCartComment = definitions['enrichedMultiCartComment']
// Item details
type MultiCartExperienceTickets = MultiCartExperienceItem['tickets']
type MultiCartLuxuryPlusSubscriptionBenefits = MultiCartLuxuryPlusSubscriptionItem['benefits']
type MultiCartTransferData = MultiCartTransferItem['transfer']
type MultiCartFlightItem = definitions['userCartFlightItem']
// App types
type MultiCartBaseItem = Pick<App.MultiCartItem, 'id' | 'state' | 'status' | 'lastModifiedAt' | 'createdBy' | 'purchasedBy' | 'item'>
// Enriched types
type BaseEnrichedItem = Omit<MultiCartItem, 'item'>
type EnrichedFlightItem = BaseEnrichedItem & { item: MultiCartFlightItem }
type EnrichedBedbankHotelItem = BaseEnrichedItem & { item: MultiCartBedbankHotelItem }
type EnrichedExperienceItem = BaseEnrichedItem & { item: MultiCartExperienceItem }
type EnrichedHotelItem = BaseEnrichedItem & { item: MultiCartHotelItem }
type EnrichedTransferItem = BaseEnrichedItem & { item: MultiCartTransferItem }
type EnrichedLuxuryPlusSubscriptionItem = BaseEnrichedItem & { item: MultiCartLuxuryPlusSubscriptionItem }
type EnrichedMerchandiseItem = BaseEnrichedItem & { item: MultiCartMerchandiseItem }

function mapUserInfo(userInfo: MultiCartAuthUser | undefined): App.MultiCartUserInfo | undefined {
  return userInfo ? {
    userId: userInfo.idMember,
    email: userInfo.email,
    givenName: userInfo.givenName ?? '',
    fullName: userInfo.fullName ?? '',
    surname: userInfo.surname ?? '',
  } : undefined
}

function mapMultiCartBaseItem(apiItem: MultiCartItem): MultiCartBaseItem {
  const { item, id, updatedAt, createdBy, purchasedBy } = apiItem
  return {
    id,
    state: item.saveForLater ? 'save_for_later' : 'active',
    // default to available on new fetch
    status: 'available',
    lastModifiedAt: updatedAt,
    createdBy: mapUserInfo(createdBy),
    // Take first user from purchasedBy array for now
    purchasedBy: mapUserInfo(purchasedBy?.[0]),
    item: item as App.Checkout.AnyItem,
  }
}

function mapMultiCartFlightItem(apiItem: EnrichedFlightItem): App.MultiCartFlightItem {
  const { item } = apiItem
  // TODO - CRO-5692 Update cart contract (remove uses of flightItem)
  const flightItem = item as any
  return {
    ...mapMultiCartBaseItem(apiItem),
    type: 'flight',
    passengers: item.passengers,
    occupants: flightItem.occupants,
    fareType: flightItem.fareType,
    carrier: flightItem.carrier,
    viewType: item.viewType,
    originAirportCode: flightItem.originAirportCode,
    destinationAirportCode: flightItem.destinationAirportCode,
    flights: flightItem.flights,
    searchId: item.searchId,
    journeyType: item.journeyType,
    isFlightCredit: flightItem.isFlightCredit,
    totalFare: flightItem.totalFare,
    otherFees: item.otherFees,
    fetchingFare: flightItem.fetchingFare,
    hasFinalFare: item.hasFinalFare,
    fareFetchError: flightItem.fareFetchError,
    bundledItemIds: flightItem.bundledItemIds,
    quotedFare: flightItem.quotedFare,
    businessTravellerCredits: flightItem.businessTravellerCredits,
    travelStart: item.travelStart,
    travelEnd: item.travelEnd,
    forceBundleId: flightItem.forceBundleId,
    expiry: item.expiry,
    selectedCabinClass: item.selectedCabinClass,
  }
}

function mapMultiCartBedbankItem(apiItem: EnrichedBedbankHotelItem): App.MultiCartBedbankHotelItem {
  const { item } = apiItem
  return {
    ...mapMultiCartBaseItem(apiItem),
    type: 'bedbankHotel',
    offerId: item.offerId,
    duration: item.duration,
    packageId: item.packageId,
    bundledItemIds: item.bundledItemIds,
    /** @todo the contract needs to be updated to start handling `App.LoyaltyStayUpgradeType` values instead of just boolean */
    // loyaltyAppliedUpgradeType: bedbankItem.hasLoyaltyBenefitRoomUpgrade,
    sessionId: item.sessionId,
    roomId: item.roomId,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    isFlightBundle: !!item.isFlightBundle,
    roomRateId: item.roomRateId,
    bedGroupId: item.bedGroupId ?? '',
    occupancy: item.occupancy,
    rateGroupId: item.rateGroupId,
    boardCode: item.boardCode,
    refundable: item.refundable,
    requestConnectedRooms: item.requestConnectedRooms,
  }
}

function mapExperienceTickets(apiTickets: MultiCartExperienceTickets): Array<App.ExperienceItemTicket> {
  return apiTickets.map((ticket): App.ExperienceItemTicket => {
    // TODO - CRO-5692 Update cart contract (remove uses of itemTicket)
    const itemTicket = ticket as any
    return {
      name: ticket.name,
      fareType: itemTicket.fareType,
      id: itemTicket.id,
      experienceId: itemTicket.experienceId,
      productId: itemTicket.productId,
      type: itemTicket.type,
      purchaseLimit: {
        min: itemTicket.purchaseLimit?.min ?? 0,
        max: itemTicket.purchaseLimit?.max ?? 0,
        maxReason: itemTicket.purchaseLimit?.maxReason,
      },
      participantsPerUnit: itemTicket.participantsPerUnit ?? 1,
      customerTicketPurchasedAmount: itemTicket.customerTicketPurchasedAmount,
      price: itemTicket.price ?? 0,
      memberPrice: itemTicket.memberPrice ?? 0,
      memberPriceCurrency: itemTicket.memberPriceCurrency ?? 'AUD',
      groupId: itemTicket.groupId ?? '',
      discount: itemTicket.discount ?? 0,
      discounts: {
        base: {
          amount: itemTicket.discounts?.base?.amount ?? 0,
          percentage: itemTicket.discounts?.base?.percentage ?? 0,
        },
        app: {
          amount: itemTicket.discounts?.app?.amount ?? 0,
          percentage: itemTicket.discounts?.app?.percentage ?? 0,
        },
      },
      specialRequests: itemTicket.specialRequests,
      date: ticket.date,
      time: itemTicket.time,
      taxesAndFees: itemTicket.taxesAndFees,
      bookByDate: itemTicket.bookByDate,
      rateStartDate: itemTicket.rateStartDate,
      rateEndDate: itemTicket.rateEndDate,
      orderItemIds: itemTicket.orderItemIds,
      isExtra: itemTicket.isExtra,
    }
  })
}

function mapMultiCartExperienceItem(apiItem: EnrichedExperienceItem): App.MultiCartExperienceItem {
  const { item } = apiItem
  // TODO - CRO-5692 Update cart contract (remove uses of experienceItem)
  const experienceItem = item as any
  return {
    ...mapMultiCartBaseItem(apiItem),
    type: 'experience',
    offerType: experienceItem.offerType,
    bookingType: experienceItem.bookingType,
    experienceId: item.experienceId,
    tickets: mapExperienceTickets(item.tickets),
    date: item.date,
    languageId: experienceItem.languageId,
    time: experienceItem.time,
    pickupPointId: item.pickupPointId,
    redemptionLocationId: item.redemptionLocationId,
    ticketModesMap: experienceItem.ticketModesMap,
    leExclusive: item.leExclusive,
    addon: experienceItem.addon,
    isBuyNowBookLater: item.isBuyNowBookLater,
    specialRequests: experienceItem.specialRequests,
    isGift: item.isGift,
    isBookingBNBL: experienceItem.isBookingBNBL,
    orderMode: experienceItem.orderMode,
  }
}

function mapMultiCartHotelItem(apiItem: EnrichedHotelItem): App.MultiCartInstantBookingLEHotelItem | undefined {
  const { item } = apiItem
  if (item.reservationType === 'instant_booking') {
    // TODO - CRO-5692 Update cart contract (remove uses of hotelItem)
    const hotelItem = item as any
    return {
      ...mapMultiCartBaseItem(apiItem),
      type: 'hotel',
      offerId: item.offerId,
      duration: item.duration,
      packageId: item.packageId,
      roomRateId: item.roomRateId,
      reservationType: 'instant_booking',
      checkIn: item.checkIn,
      checkOut: item.checkOut,
      occupancy: item.occupancy,
      bundledItemIds: hotelItem.bundledItemIds,
      requestConnectedRooms: item.requestConnectedRooms,
      /** @todo the contract needs to be updated to start handling `App.LoyaltyStayUpgradeType` values instead of just boolean */
      // loyaltyAppliedUpgradeType: hotelItem.hasLoyaltyBenefitRoomUpgrade,
    }
  }
}

function mapTransferTypes(transferTypes: Array<string>): Array<App.ExperienceTransferType> {
  const validTypes: Array<App.ExperienceTransferType> = ['AIRPORT-TO-HOTEL', 'HOTEL-TO-AIRPORT']
  return transferTypes
    .filter((type): type is App.ExperienceTransferType =>
      validTypes.includes(type as App.ExperienceTransferType),
    )
}

function mapMaxReason(maxReason: string): App.ExperiencePurchaseLimit['maxReason'] {
  const validReasons: Array<App.ExperiencePurchaseLimit['maxReason']> = ['AVAILABILITY', 'LIMIT-PER-ORDER', 'LIMIT-PER-CUSTOMER']
  return maxReason && validReasons.includes(maxReason as App.ExperiencePurchaseLimit['maxReason'])
    ? maxReason as App.ExperiencePurchaseLimit['maxReason']
    : 'AVAILABILITY'
}

function mapTransfer(apiTransfer: MultiCartTransferData): App.ExperienceTransferView {
  // TODO - CRO-5692 Update cart contract (remove uses of rawTransfer)
  const rawTransfer = apiTransfer as any
  return {
    date: apiTransfer.date,
    time: apiTransfer.time,
    flightNumber: apiTransfer.flightNumber,
    hotel: {
      googlePlaceId: apiTransfer.hotel?.googlePlaceId,
      name: apiTransfer.hotel?.name,
      kmFromAirport: apiTransfer.hotel?.kmFromAirport,
      latitude: apiTransfer.hotel?.latitude,
      longitude: apiTransfer.hotel?.longitude,
    },
    travellers: {
      adults: apiTransfer.travellers?.adults ?? 0,
      children: apiTransfer.travellers?.children ?? 0,
      childSeats: apiTransfer.travellers?.childSeats ?? 0,
      boosterSeats: apiTransfer.travellers?.boosterSeats ?? 0,
    },
    type: rawTransfer.type,
    option: apiTransfer.option ? {
      id: apiTransfer.option.id,
      name: apiTransfer.option.name,
      price: apiTransfer.option.price ?? 0,
      memberPrice: apiTransfer.option.memberPrice ?? 0,
      memberPriceCurrency: rawTransfer.option.memberPriceCurrency ?? 'AUD',
      description: apiTransfer.option.description ?? '',
      experienceId: apiTransfer.option.experienceId,
      maxPassengers: apiTransfer.option.maxPassengers ?? 0,
      maxCheckedBags: apiTransfer.option.maxCheckedBags ?? 0,
      maxCarryOnBags: apiTransfer.option.maxCarryOnBags ?? 0,
      hasChildSeats: apiTransfer.option.hasChildSeats ?? false,
      hasBoosterSeats: apiTransfer.option.hasBoosterSeats ?? false,
      maxTransferDistance: apiTransfer.option.maxTransferDistance ?? 100,
      transferTypes: mapTransferTypes(apiTransfer.option.transferTypes ?? []),
      taxesAndFees: apiTransfer.option.taxesAndFees,
      purchaseLimit: {
        min: apiTransfer.option.purchaseLimit.min ?? 0,
        max: apiTransfer.option.purchaseLimit.max ?? 0,
        maxReason: mapMaxReason(apiTransfer.option.purchaseLimit.maxReason),
      },
      bookByDate: apiTransfer.option.bookByDate,
      rateStartDate: apiTransfer.option.rateStartDate,
      rateEndDate: apiTransfer.option.rateEndDate,
    } : undefined,
    title: apiTransfer.title,
    specialRequests: rawTransfer.specialRequests,
    bookByDate: rawTransfer.bookByDate,
    rateEndDate: rawTransfer.rateEndDate,
    baggage: {
      bags: rawTransfer.baggage?.bags ?? 0,
      oversizedBags: rawTransfer.baggage?.oversizedBags ?? 0,
    },
  }
}

function mapMultiCartTransferItem(apiItem: EnrichedTransferItem): App.MultiCartTransferItem {
  const { item } = apiItem
  // TODO - CRO-5692 Update cart contract (remove uses of transferItem)
  const transferItem = item as any
  return {
    ...mapMultiCartBaseItem(apiItem),
    type: 'transfer',
    offerType: transferItem.offerType,
    experienceId: item.experienceId,
    transfer: mapTransfer(item.transfer),
  }
}

function isSupportedBenefit(serverBenefit: any): boolean {
  return SUPPORTED_BENEFIT_TYPES.includes(serverBenefit.type)
}

function mapBaseBenefitsToLocal(serverBenefit: any): App.OfferSubscriptionBenefitItem {
  return {
    type: serverBenefit.type,
    title: serverBenefit.title,
    shortTitle: serverBenefit.shortTitle,
    description: serverBenefit.description,
    usedAmount: 0,
    allocatedAmount: serverBenefit.allocatedAmount,
  }
}

function mapSubscriptionBenefits(apiBenefits: MultiCartLuxuryPlusSubscriptionBenefits): Array<App.OfferSubscriptionBenefitItem> {
  return apiBenefits
    .filter(isSupportedBenefit)
    .map(mapBaseBenefitsToLocal)
}

function mapMultiCartLuxuryPlusSubscriptionItem(apiItem: EnrichedLuxuryPlusSubscriptionItem): App.MultiCartLuxPlusSubscriptionItem {
  const { item } = apiItem
  return {
    ...mapMultiCartBaseItem(apiItem),
    type: 'luxury-plus-subscription',
    amount: item.amount,
    benefits: mapSubscriptionBenefits(item.benefits),
    offerId: item.offerId,
    tier: item.tier,
  }
}

function mapMultiCartMerchandiseItem(apiItem: EnrichedMerchandiseItem): App.MultiCartMerchandiseItem {
  const { item } = apiItem
  // Cast to any to access properties that may not be in the current contract definition
  const merchandiseItem = item as any
  return {
    ...mapMultiCartBaseItem(apiItem),
    type: 'merchandise',
    itemId: item.itemId,
    offerId: merchandiseItem.offerId ?? item.advertId, // Try offerId first, fallback to advertId
    variantId: item.variantId,
    transactionKey: item.transactionKey,
    shippingProfileId: merchandiseItem.shippingProfileId ?? '', // Access via any cast since it's not in contract
  }
}

export function mapMultiCartsResponse(response: MultiCartGetResponse): Array<App.MultiCartSummary> {
  return response.multiCarts.map((cart): App.MultiCartSummary => ({
    cartId: cart.multiCartId,
    name: cart.name === 'default' ? 'My cart' : cart.name,
    type: cart.type,
  }))
}

export function mapMultiCartCreateResponse(response: MultiCartCreateResponse): App.MultiCartSummary {
  return {
    cartId: response.result.id,
    name: response.result.name,
    type: response.result.type,
  }
}

export function mapMultiCartMemberResponse(response: MultiCartGetMemberResponse): Array<App.MultiCartMember> {
  return response.users.map((user): App.MultiCartMember => ({
    accessLevel: user.access,
    accessStatus: user.status,
    userId: user.userId ?? undefined,
    email: user.email,
    fullName: user.fullName ?? undefined,
  }))
}

export function mapRevokeMultiCartMemberResponse(response: MultiCartRevokeUserAccessResponse): { cartId: string, user: App.MultiCartMember } {
  return {
    cartId: response.user.multiCartId,
    user: {
      accessLevel: response.user.access,
      accessStatus: response.user.status,
      userId: response.user.userId ?? undefined,
      email: response.user.email,
    },
  }
}

export function mapMultiCartItem(apiItem: MultiCartItem): App.MultiCartItem | undefined {
  const { item } = apiItem
  // Handle different item types based on the translated type
  switch (item.itemType) {
    case 'flight':
      return mapMultiCartFlightItem({
        ...apiItem,
        item,
      })
    case 'hotel':
      return mapMultiCartHotelItem({
        ...apiItem,
        item,
      })
    case 'bedbankHotel':
      return mapMultiCartBedbankItem({
        ...apiItem,
        item,
      })
    case 'experience':
      return mapMultiCartExperienceItem({
        ...apiItem,
        item,
      })
    case 'transfer':
      return mapMultiCartTransferItem({
        ...apiItem,
        item,
      })
    case 'luxury-plus-subscription':
      return mapMultiCartLuxuryPlusSubscriptionItem({
        ...apiItem,
        item,
      })
    case 'merchandise':
      return mapMultiCartMerchandiseItem({
        ...apiItem,
        item,
      })
    default:
      // For unknown types, return nothing but report to sentry, this shouldn't happen
      reportClientError(new Error('MultiCart unsupported item received from server'), { ...item })
      return
  }
}

export function mapMultiCartItemResponse(response: MultiCartItemGetResponse): Array<App.MultiCartItem> {
  return nonNullable(response.result.items.map(mapMultiCartItem))
}

export function mapMultiCartAddItemResponse(response: MultiCartAddItemResponse): Array<App.MultiCartItem> {
  return nonNullable(response.result.map(mapMultiCartItem))
}

export function mapMultiCartUpdateItemResponse(response: MultiCartUpdateItemResponse): Array<App.MultiCartItem> {
  return nonNullable(response.result.map(mapMultiCartItem))
}

export function mapMultiCartOfferComparisonItem(item: CartOfferComparisonItem): App.OfferComparisonSelection {
  return {
    selectionId: item.selectionId,
    offerType: mapToOfferType(item.offerType),
    offerId: item.offerId,
    name: item.name,
    label: item.label,
    leadOfferImageId: item.leadOfferImageId,
    priceTotals: {
      price: item.priceTotals?.price ?? 0,
      memberPrice: item.priceTotals?.memberPrice ?? 0,
      value: item.priceTotals?.value ?? 0,
    },
    params: {
      packageUniqueKey: item.params?.packageUniqueKey,
      roomId: item.params?.roomId,
      packageId: item.params?.packageId,
      filters: item.params?.filters as { [key: string]: unknown },
    },
  }
}

// Comment mappers
function mapMultiCartComment(comment: MultiCartComment): App.MultiCartComment {
  return {
    id: comment.id,
    multiCartId: comment.multiCartId,
    itemId: comment.itemId,
    text: comment.text,
    createdBy: {
      idMember: comment.createdBy.idMember,
      email: comment.createdBy.email,
      givenName: comment.createdBy.givenName ?? undefined,
      fullName: comment.createdBy.fullName ?? undefined,
      surname: comment.createdBy.surname ?? undefined,
    },
    createdAt: comment.createdAt,
    updatedAt: comment.updatedAt,
    editedAt: comment.editedAt ?? undefined,
  }
}

export function mapMultiCartCommentResponse(response: MultiCartCommentResponse): Array<App.MultiCartComment> {
  return (response.result ?? []).map(mapMultiCartComment)
}

export function mapMultiCartCreateCommentResponse(response: MultiCartCreateCommentResponse): App.MultiCartComment {
  return mapMultiCartComment(response.result)
}

export function mapMultiCartUpdateCommentResponse(response: MultiCartUpdateCommentResponse): App.MultiCartComment {
  return mapMultiCartComment(response.result)
}
